import React, { useState, useRef } from 'react';
import { XMarkIcon, DocumentArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MemberService } from '../services/firebaseService';

interface ImportMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (importedCount: number) => void;
}

interface ImportOptions {
  duplicateAction: 'skip' | 'update';
}

interface ParsedMember {
  firstName: string;
  lastName: string;
  email: string;
  membershipPlan?: string;
  membershipStatus?: string;
  memberSince?: string;
  lastActive?: string;
  isValid: boolean;
  errors: string[];
}

const ImportMembersModal: React.FC<ImportMembersModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedMember[]>([]);
  const [importing, setImporting] = useState(false);
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    duplicateAction: 'skip'
  });
  const [importResults, setImportResults] = useState<{
    success: number;
    updated: number;
    skipped: number;
    errors: Array<{ email: string; error: string }>;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fonction pour parser une ligne CSV en gérant les guillemets
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Vérifier l'extension du fichier
      const fileName = selectedFile.name.toLowerCase();
      if (fileName.endsWith('.csv') || selectedFile.type === 'text/csv' || selectedFile.type === 'application/vnd.ms-excel') {
        setFile(selectedFile);
        parseCSV(selectedFile);
      } else {
        alert('Veuillez sélectionner un fichier CSV valide (.csv)');
      }
    }
  };

  const parseCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        if (!csv || csv.trim().length === 0) {
          alert('Le fichier CSV est vide');
          return;
        }

        const lines = csv.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          alert('Le fichier CSV doit contenir au moins un en-tête et une ligne de données');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
        console.log('En-têtes détectés:', headers);
        
        const requiredHeaders = ['member_first_name', 'member_last_name', 'member_email'];
        
        // Vérifier les en-têtes requis
        const missingHeaders = requiredHeaders.filter(header => 
          !headers.some(h => h.includes(header))
        );
        
        if (missingHeaders.length > 0) {
          alert(`En-têtes manquants : ${missingHeaders.join(', ')}\n\nEn-têtes détectés : ${headers.join(', ')}`);
          return;
        }

      const data: ParsedMember[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        // Parser les valeurs en gérant les guillemets
        const values = parseCSVLine(lines[i]);
        const member: ParsedMember = {
          firstName: '',
          lastName: '',
          email: '',
          membershipPlan: '',
          membershipStatus: '',
          memberSince: '',
          lastActive: '',
          isValid: true,
          errors: []
        };

        // Mapper les colonnes
        headers.forEach((header, index) => {
          const value = values[index] || '';
          
          if (header.includes('member_first_name')) {
            member.firstName = value;
          } else if (header.includes('member_last_name')) {
            member.lastName = value;
          } else if (header.includes('member_email')) {
            member.email = value;
          } else if (header.includes('membership_plan')) {
            member.membershipPlan = value;
          } else if (header.includes('membership_status')) {
            member.membershipStatus = value;
          } else if (header.includes('member_since')) {
            member.memberSince = value;
          } else if (header.includes('member_last_active')) {
            member.lastActive = value;
          }
        });

        // Valider les données
        if (!member.firstName.trim()) {
          member.errors.push('Prénom requis');
          member.isValid = false;
        }
        if (!member.lastName.trim()) {
          member.errors.push('Nom requis');
          member.isValid = false;
        }
        if (!member.email.trim()) {
          member.errors.push('Email requis');
          member.isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
          member.errors.push('Format email invalide');
          member.isValid = false;
        }
        
        // Valider les dates si présentes
        if (member.memberSince && member.memberSince.trim()) {
          const cleanDate = member.memberSince.replace(/['"]/g, '').trim();
          const testDate = new Date(cleanDate);
          if (isNaN(testDate.getTime())) {
            member.errors.push(`Date member_since invalide: ${cleanDate}`);
            member.isValid = false;
          }
        }
        
        if (member.lastActive && member.lastActive.trim()) {
          const cleanDate = member.lastActive.replace(/['"]/g, '').trim();
          const testDate = new Date(cleanDate);
          if (isNaN(testDate.getTime())) {
            member.errors.push(`Date member_last_active invalide: ${cleanDate}`);
            member.isValid = false;
          }
        }

        data.push(member);
      }

        console.log(`Parsing terminé. ${data.length} membres parsés sur ${lines.length - 1} lignes de données`);
        setParsedData(data);
      } catch (error) {
        console.error('Erreur lors du parsing CSV:', error);
        alert('Erreur lors de la lecture du fichier CSV. Vérifiez que le fichier est bien formaté.');
      }
    };
    reader.onerror = () => {
      alert('Erreur lors de la lecture du fichier');
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImport = async () => {
    if (parsedData.length === 0) return;

    const validMembers = parsedData.filter(m => m.isValid);
    if (validMembers.length === 0) {
      alert('Aucun membre valide à importer');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const results = {
        success: 0,
        updated: 0,
        skipped: 0,
        errors: [] as Array<{ email: string; error: string }>
      };

      console.log(`Début de l'import de ${validMembers.length} membres valides`);
      
      for (let i = 0; i < validMembers.length; i++) {
        const member = validMembers[i];
        console.log(`Traitement du membre ${i + 1}/${validMembers.length}: ${member.email}`);
        
        try {
          // Convertir le statut d'adhésion
          let membershipStatus: 'active' | 'inactive' | 'paused' = 'active';
          if (member.membershipStatus) {
            if (member.membershipStatus === 'free_trial' || member.membershipStatus === 'active') {
              membershipStatus = 'active';
            } else if (member.membershipStatus === 'paused') {
              membershipStatus = 'paused';
            } else {
              membershipStatus = 'inactive';
            }
          }

          // Convertir les dates avec gestion d'erreur
          let memberSince: Date;
          let lastActive: Date;
          
          try {
            // Nettoyer les dates (enlever les guillemets et espaces)
            const cleanMemberSince = member.memberSince?.replace(/['"]/g, '').trim();
            const cleanLastActive = member.lastActive?.replace(/['"]/g, '').trim();
            
            memberSince = cleanMemberSince ? new Date(cleanMemberSince) : new Date();
            lastActive = cleanLastActive ? new Date(cleanLastActive) : new Date();
            
            // Vérifier si les dates sont valides
            if (isNaN(memberSince.getTime())) {
              console.warn(`Date member_since invalide pour ${member.email}: ${cleanMemberSince}`);
              memberSince = new Date();
            }
            if (isNaN(lastActive.getTime())) {
              console.warn(`Date member_last_active invalide pour ${member.email}: ${cleanLastActive}`);
              lastActive = new Date();
            }
          } catch (error) {
            console.warn(`Erreur de parsing des dates pour ${member.email}:`, error);
            memberSince = new Date();
            lastActive = new Date();
          }

          // Vérifier si le membre existe déjà
          const existingMember = await MemberService.getMemberByEmail(member.email);
          
          if (existingMember) {
            // Membre existe déjà
            if (importOptions.duplicateAction === 'update') {
              // Mettre à jour le membre existant
              await MemberService.updateMember(existingMember.id, {
                firstName: member.firstName,
                lastName: member.lastName,
                department: member.membershipPlan || existingMember.department,
                membershipStatus,
                hasVotingRights: membershipStatus === 'active',
                lastActive
              });
              results.updated++;
            } else {
              // Ignorer le membre existant
              results.skipped++;
            }
          } else {
            // Créer un nouveau membre
            await MemberService.createMember({
              firstName: member.firstName,
              lastName: member.lastName,
              email: member.email,
              phone: '',
              department: member.membershipPlan || '',
              membershipStatus,
              hasVotingRights: membershipStatus === 'active',
              memberSince,
              lastActive
            });
            results.success++;
          }
        } catch (error: any) {
          console.error('Erreur pour le membre:', member.email, error);
          results.errors.push({
            email: member.email,
            error: error.message || 'Erreur inconnue'
          });
        }
      }

      console.log(`Import terminé. Résultats:`, results);
      setImportResults(results);
      onImportComplete(results.success + results.updated);
    } catch (error) {
      console.error('Erreur lors de l\'import:', error);
      alert('Erreur lors de l\'import des membres');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setImportResults(null);
    onClose();
  };

  const validMembers = parsedData.filter(m => m.isValid);
  const invalidMembers = parsedData.filter(m => !m.isValid);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? 'block' : 'hidden'}`}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <DocumentArrowUpIcon className="w-6 h-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Importer des Membres</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!file ? (
            /* Étape 1: Sélection du fichier */
            <div className="text-center">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sélectionner un fichier CSV</h3>
                <p className="text-gray-600">Le fichier doit contenir les colonnes : Prénom, Nom, Email</p>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-primary"
                >
                  <DocumentArrowUpIcon className="w-5 h-5 mr-2" />
                  Choisir un fichier CSV
                </button>
              </div>

              <div className="mt-6 text-sm text-gray-500">
                <p><strong>Format CSV attendu (format ASPROCOM) :</strong></p>
                <p>member_first_name, member_last_name, member_email, membership_plan, membership_status, member_since, member_last_active</p>
              </div>
            </div>
          ) : (
            /* Étape 2: Prévisualisation et import */
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Prévisualisation des données</h3>
                <p className="text-gray-600">
                  Fichier sélectionné : <strong>{file.name}</strong>
                </p>
              </div>

              {/* Statistiques */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{parsedData.length}</div>
                  <div className="text-sm text-blue-800">Total lignes</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{validMembers.length}</div>
                  <div className="text-sm text-green-800">Valides</div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">{invalidMembers.length}</div>
                  <div className="text-sm text-red-800">Invalides</div>
                </div>
              </div>

              {/* Options d'import */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Options d'import :</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="duplicateAction"
                      value="skip"
                      checked={importOptions.duplicateAction === 'skip'}
                      onChange={(e) => setImportOptions({...importOptions, duplicateAction: e.target.value as 'skip' | 'update'})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Ignorer les membres existants (recommandé)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="duplicateAction"
                      value="update"
                      checked={importOptions.duplicateAction === 'update'}
                      onChange={(e) => setImportOptions({...importOptions, duplicateAction: e.target.value as 'skip' | 'update'})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Mettre à jour les membres existants</span>
                  </label>
                </div>
              </div>

              {/* Résultats d'import */}
              {importResults && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Résultats de l'import :</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center text-green-600">
                      <CheckCircleIcon className="w-5 h-5 mr-2" />
                      <span>{importResults.success} nouveaux</span>
                    </div>
                    {importResults.updated > 0 && (
                      <div className="flex items-center text-blue-600">
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        <span>{importResults.updated} mis à jour</span>
                      </div>
                    )}
                    {importResults.skipped > 0 && (
                      <div className="flex items-center text-yellow-600">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        <span>{importResults.skipped} ignorés</span>
                      </div>
                    )}
                    {importResults.errors.length > 0 && (
                      <div className="flex items-center text-red-600">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        <span>{importResults.errors.length} erreurs</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tableau de prévisualisation */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Prénom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Nom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan d'adhésion
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parsedData.slice(0, 10).map((member, index) => (
                      <tr key={index} className={member.isValid ? '' : 'bg-red-50'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {member.isValid ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          ) : (
                            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.firstName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.lastName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.membershipPlan || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {member.membershipStatus || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    ... et {parsedData.length - 10} autres lignes
                  </p>
                )}
              </div>

              {/* Erreurs de validation */}
              {invalidMembers.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-red-800 mb-2">Erreurs de validation :</h4>
                  <div className="space-y-2">
                    {invalidMembers.slice(0, 5).map((member, index) => (
                      <div key={index} className="text-sm text-red-600">
                        <strong>{member.firstName} {member.lastName}</strong> : {member.errors.join(', ')}
                      </div>
                    ))}
                    {invalidMembers.length > 5 && (
                      <p className="text-sm text-gray-500">... et {invalidMembers.length - 5} autres erreurs</p>
                    )}
                  </div>
                </div>
              )}

              {/* Erreurs d'import */}
              {importResults && importResults.errors.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-red-800 mb-2">Erreurs d'import :</h4>
                  <div className="space-y-2">
                    {importResults.errors.slice(0, 5).map((error, index) => (
                      <div key={index} className="text-sm text-red-600">
                        <strong>{error.email}</strong> : {error.error}
                      </div>
                    ))}
                    {importResults.errors.length > 5 && (
                      <p className="text-sm text-gray-500">... et {importResults.errors.length - 5} autres erreurs</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="btn-secondary"
          >
            Annuler
          </button>
          
          {file && validMembers.length > 0 && (
            <button
              onClick={handleImport}
              disabled={importing}
              className="btn-primary flex items-center"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Import en cours...
                </>
              ) : (
                <>
                  <DocumentArrowUpIcon className="w-4 h-4 mr-2" />
                  Importer {validMembers.length} membres
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportMembersModal;
